import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Text } from '@/components/ui';
import { fetchAccounts, fetchAllTransactions, fetchCategories } from '@/lib/finance/api';
import { buildAccountActivity, type ActivityCell } from '@/lib/finance/activity';
import { accountPickerLabel } from '@/lib/finance/format';
import { monthEnd, monthLabelShort, monthStart, recentMonths } from '@/lib/finance/month';
import type { Account, Category, Transaction } from '@/lib/finance/types';

const WINDOW_MONTHS = 12;

function cellTitle(cell: ActivityCell): string {
  if (cell.count === 0) return 'No transactions';
  return `${cell.count} transaction${cell.count === 1 ? '' : 's'}`;
}

export function AccountActivityReport({ month }: { month: string }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const months = useMemo(() => recentMonths(month, WINDOW_MONTHS), [month]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [accountRows, categoryRows, transactionRows] = await Promise.all([
          fetchAccounts(),
          fetchCategories(),
          fetchAllTransactions({ from: monthStart(months[0]!), to: monthEnd(month) }),
        ]);
        if (cancelled) return;
        setAccounts(accountRows);
        setCategories(categoryRows);
        setTransactions(transactionRows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month, months]);

  const report = useMemo(
    () => buildAccountActivity(accounts, categories, transactions, months),
    [accounts, categories, transactions, months],
  );

  if (loading) return <ActivityIndicator />;
  if (error)
    return (
      <Text variant="body-sm" className="text-danger">
        {error}
      </Text>
    );

  return (
    <View>
      <Text variant="h3" className="mb-2">
        Account activity · last {WINDOW_MONTHS} months
      </Text>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="text-fg-muted">
              <th className="border-b border-border px-3 py-2 text-left font-medium">Account</th>
              {months.map((item) => (
                <th
                  key={item}
                  className={`border-b border-border px-2 py-2 text-center font-medium ${
                    item === month ? 'text-accent' : ''
                  }`}
                >
                  {monthLabelShort(item)}
                </th>
              ))}
              <th className="border-b border-border px-3 py-2 text-center font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-fg-muted" colSpan={months.length + 2}>
                  No accounts yet.
                </td>
              </tr>
            ) : (
              <>
                {report.rows.map((row) => (
                  <tr key={row.accountId ?? 'unassigned'}>
                    <td className="border-b border-border px-3 py-2 text-left">
                      <div className="font-medium">
                        {row.accountId == null
                          ? row.label
                          : accountPickerLabel(
                              accounts.find((account) => account.id === row.accountId) ?? {
                                name: row.label,
                              },
                            )}
                      </div>
                      {row.currencyCode ? (
                        <div className="text-xs text-fg-muted">{row.currencyCode}</div>
                      ) : null}
                      {!row.isActive ? <div className="text-xs text-fg-muted">Inactive</div> : null}
                    </td>
                    {row.cells.map((cell, index) => {
                      const quiet = cell.count === 0;
                      return (
                        <td
                          key={months[index]}
                          title={cellTitle(cell)}
                          className={`border-b border-border px-2 py-1.5 text-center tabular-nums ${
                            quiet ? 'bg-warning-muted text-warning' : 'bg-success-muted text-fg'
                          }`}
                        >
                          <div className="font-semibold">{cell.count}</div>
                        </td>
                      );
                    })}
                    <td className="border-b border-border px-2 py-1.5 text-center font-semibold tabular-nums">
                      {row.totalCount}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="border-t-2 border-border px-3 py-2 text-left">All accounts</td>
                  {report.countsByMonth.map((count, index) => (
                    <td
                      key={months[index]}
                      className="border-t-2 border-border px-2 py-1.5 text-center tabular-nums"
                    >
                      {count}
                    </td>
                  ))}
                  <td className="border-t-2 border-border px-2 py-1.5 text-center tabular-nums">
                    {report.totalCount}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <Text variant="caption" className="mt-3 text-muted">
        Green cells have transactions. Amber cells have none, so a missing month stands out. Each
        number is a transaction count. A transfer is counted on both accounts. The total row is
        distinct transactions.
      </Text>
    </View>
  );
}
